let getTokenFn: (() => Promise<string>) | null = null
let tokenReady: Promise<void>
let resolveTokenReady: () => void

// Simple token cache to avoid hammering Auth0 on rapid requests
let cachedToken: string | null = null
let cacheExpiresAt = 0
const TOKEN_CACHE_MS = 30_000 // cache token for 30 seconds

// Deduplicate concurrent getAccessTokenSilently() calls — only one in-flight at a time
let inflightTokenRequest: Promise<string> | null = null

// Create a promise that resolves when setTokenGetter is called
const initTokenPromise = () => {
  tokenReady = new Promise<void>((resolve) => {
    resolveTokenReady = resolve
  })
}
initTokenPromise()

export function setTokenGetter(fn: () => Promise<string>) {
  getTokenFn = fn
  resolveTokenReady()
}

async function getHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {}
  if (getTokenFn) {
    try {
      const now = Date.now()
      if (cachedToken && now < cacheExpiresAt) {
        headers['Authorization'] = `Bearer ${cachedToken}`
      } else {
        // Deduplicate: if a token request is already in-flight, reuse it
        if (!inflightTokenRequest) {
          inflightTokenRequest = getTokenFn().finally(() => {
            inflightTokenRequest = null
          })
        }
        const token = await inflightTokenRequest
        if (token) {
          cachedToken = token
          cacheExpiresAt = Date.now() + TOKEN_CACHE_MS
          headers['Authorization'] = `Bearer ${token}`
        }
      }
    } catch {
      // Not authenticated
      cachedToken = null
      cacheExpiresAt = 0
    }
  }
  return headers
}

/** Wait for token getter to be set, with timeout to avoid blocking anonymous users */
/** Resolves true once the token getter is wired, false if the wait ran out.
 *
 * The timeout used to be invisible: callers awaited a `void` and carried on,
 * and `getHeaders()` sends no `Authorization` when the getter is still
 * missing — so a slow Auth0 turned a signed-in user's request into an
 * anonymous one, which came back 401 and was swallowed. A caller that needs
 * the token can now see that it never arrived and wait again instead of
 * asking the server as a stranger. */
export async function waitForAuth(timeoutMs = 2000): Promise<boolean> {
  return Promise.race([
    tokenReady.then(() => true),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ])
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await getHeaders()
  const res = await fetch(`/api${path}`, { headers })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.detail ?? null, res.statusText)
  }
  return res.json()
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const headers = await getHeaders()
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new ApiError(res.status, data.detail ?? null, res.statusText)
  }
  return res.json()
}

export async function apiPostForm<T>(path: string, formData: FormData): Promise<T> {
  const headers = await getHeaders()
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers,
    body: formData,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new ApiError(res.status, data.detail ?? null, res.statusText)
  }
  return res.json()
}

/** Multipart POST via XHR — the only way to observe upload progress. */
/**
 * The upload was stopped by the person waiting for it.
 *
 * Its own type, not an `ApiError`: a cancelled request must not be retried
 * and must not put a failure on screen. Both of those are decided by looking
 * at the error, so the difference has to survive being thrown.
 */
export class CancelledError extends Error {
  constructor() {
    super('Cancelled')
    this.name = 'CancelledError'
  }
}

export async function apiUploadForm<T>(
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  const headers = await getHeaders()
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new CancelledError())
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api${path}`)
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value as string)
    }
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }
    xhr.onload = () => {
      let body: { detail?: string } = {}
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        // non-JSON body
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T)
      else reject(new ApiError(xhr.status, body.detail ?? null, xhr.statusText))
    }
    // Status 0, and `detail` deliberately NULL: "Network error" is OUR
    // sentence, not the API's, and `detail` is the slot callers quote
    // verbatim — putting it there rendered an English literal to a Russian
    // reader, past i18n. It goes in the message instead, where analytics
    // reads it.
    xhr.onerror = () => reject(new ApiError(0, null, 'Network error'))
    // The part in flight is dropped where it stands. Nothing is charged for
    // it: a chunked job only spends a transcription when its last part lands.
    xhr.onabort = () => reject(new CancelledError())
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(formData)
  })
}

export class ApiError extends Error {
  status: number
  /**
   * The server's own `detail`, or null when the body wasn't ours.
   *
   * Worth separating from `message`, because "the API said why" and "some
   * box between us and the API said something" are different things and only
   * the first is safe to show a user verbatim. A 502 from Cloudflare carries
   * an HTML page, not our JSON, so `detail` is null and the caller knows to
   * use its own wording.
   */
  detail: string | null

  /**
   * `message` is never empty. It used to fall back to `statusText`, which is
   * the empty string on **every** HTTP/2 response — the protocol dropped the
   * reason phrase — so a non-JSON error produced `new Error("")`. Callers
   * doing `err.message` then rendered nothing at all: the upload failed, the
   * form looked untouched, and people pressed the button again.
   */
  constructor(status: number, detail: string | null, statusText = '') {
    super(detail || statusText || `Request failed (${status})`)
    this.status = status
    this.detail = detail
    this.name = 'ApiError'
  }
}
